/**
 * QuizFiltersBar Component
 * Filter bar with search, course filter, and sort options
 */

import React from 'react';
import styled from 'styled-components';
import { FiSearch } from 'react-icons/fi';

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
    width: 100%;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  width: 18px;
  height: 18px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 0.875rem;
  background-color: var(--background-primary);
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: var(--transition);
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 180px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: auto;
  }
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const FilterSelect = styled.select`
  flex: 1;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 0.875rem;
  background-color: var(--background-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: var(--transition);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1.25em 1.25em;
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

function QuizFiltersBar({ 
  searchTerm, 
  onSearchChange, 
  courseFilter, 
  onCourseFilterChange, 
  courses = [],
  sortBy,
  onSortChange,
  showSort = true,
  showSearch = true
}) {
  return (
    <FiltersContainer>
      {showSearch && (
        <SearchBar>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search quizzes..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search quizzes"
          />
        </SearchBar>
      )}
      
      <FilterGroup>
        <FilterLabel htmlFor="course-filter">Course:</FilterLabel>
        <FilterSelect
          id="course-filter"
          value={courseFilter}
          onChange={(e) => onCourseFilterChange(e.target.value)}
          aria-label="Filter by course"
        >
          <option value="all">All Courses</option>
          <option value="active">Active Courses</option>
          <option value="inactive">Inactive Courses</option>
          {courses.length > 0 && (
            <>
              <option disabled>──────────</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.code} - {course.name}
                </option>
              ))}
            </>
          )}
        </FilterSelect>
      </FilterGroup>
      
      {showSort && (
        <FilterGroup>
          <FilterLabel htmlFor="sort-filter">Sort:</FilterLabel>
          <FilterSelect
            id="sort-filter"
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            aria-label="Sort quizzes"
          >
            <option value="recent">Recently updated</option>
            <option value="created">Created date</option>
            <option value="title">Title A–Z</option>
          </FilterSelect>
        </FilterGroup>
      )}
    </FiltersContainer>
  );
}

export default QuizFiltersBar;
